import { useRef, useState } from 'react';
import { zipSync, strToU8 } from 'fflate';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

const variants = [
  {
    id: 'dark-on-light',
    label: 'Dark on White',
    bg: 'bg-white',
    border: 'border border-[#E8E6E3]',
    monogram: 'text-[#2C2C2C]',
    wordmark1: 'text-[#2C2C2C]',
    wordmark2: 'text-[#8B7355]',
    divider: 'bg-[#2C2C2C]/30',
    // raw color values for inline HTML embed
    bgColor: '#FFFFFF',
    monogramColor: '#2C2C2C',
    wordmark1Color: '#2C2C2C',
    wordmark2Color: '#8B7355',
    dividerColor: 'rgba(44,44,44,0.3)',
  },
  {
    id: 'light-on-dark',
    label: 'Light on Dark',
    bg: 'bg-[#1A1A1A]',
    border: '',
    monogram: 'text-white',
    wordmark1: 'text-white',
    wordmark2: 'text-[#C9A96E]',
    divider: 'bg-white/40',
    bgColor: '#1A1A1A',
    monogramColor: '#FFFFFF',
    wordmark1Color: '#FFFFFF',
    wordmark2Color: '#C9A96E',
    dividerColor: 'rgba(255,255,255,0.4)',
  },
  {
    id: 'gold-on-dark',
    label: 'Gold on Dark',
    bg: 'bg-[#2C2C2C]',
    border: '',
    monogram: 'text-[#C9A96E]',
    wordmark1: 'text-[#C9A96E]',
    wordmark2: 'text-white',
    divider: 'bg-[#C9A96E]/40',
    bgColor: '#2C2C2C',
    monogramColor: '#C9A96E',
    wordmark1Color: '#C9A96E',
    wordmark2Color: '#FFFFFF',
    dividerColor: 'rgba(201,169,110,0.4)',
  },
  {
    id: 'dark-on-cream',
    label: 'Dark on Cream',
    bg: 'bg-[#F7F4EF]',
    border: '',
    monogram: 'text-[#2C2C2C]',
    wordmark1: 'text-[#2C2C2C]',
    wordmark2: 'text-[#8B7355]',
    divider: 'bg-[#2C2C2C]/30',
    bgColor: '#F7F4EF',
    monogramColor: '#2C2C2C',
    wordmark1Color: '#2C2C2C',
    wordmark2Color: '#8B7355',
    dividerColor: 'rgba(44,44,44,0.3)',
  },
];

type Variant = typeof variants[0];

function buildEmbedCode(v: Variant): string {
  return `<!-- Higher Standard Global Logo — ${v.label} -->
<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
  <tr>
    <td style="background:${v.bgColor};padding:24px 40px;border-radius:8px;">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="font-family:'Cormorant Garamond',Georgia,serif;font-size:40px;font-weight:600;letter-spacing:0.15em;line-height:1;color:${v.monogramColor};vertical-align:middle;padding-right:16px;">
            HSG
          </td>
          <td style="width:1px;background:${v.dividerColor};vertical-align:middle;padding:0 16px 0 0;">
            <div style="width:1px;height:48px;background:${v.dividerColor};"></div>
          </td>
          <td style="vertical-align:middle;padding-left:4px;">
            <div style="font-family:Arial,Helvetica,sans-serif;font-size:11px;font-weight:500;letter-spacing:0.22em;text-transform:uppercase;color:${v.wordmark1Color};line-height:1;margin-bottom:6px;">
              Higher Standard
            </div>
            <div style="font-family:Arial,Helvetica,sans-serif;font-size:11px;font-weight:300;letter-spacing:0.35em;text-transform:uppercase;color:${v.wordmark2Color};line-height:1;">
              Global
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

// Favicon Generator Component
function FaviconGenerator({ activeVariant }: { activeVariant: Variant }) {
  const [faviconCopied, setFaviconCopied] = useState(false);
  const [showFaviconCode, setShowFaviconCode] = useState(false);
  const [selectedSize, setSelectedSize] = useState<16 | 32 | 180>(32);
  const [zipDownloading, setZipDownloading] = useState(false);

  const faviconSizes = [
    { size: 16, label: '16×16', desc: 'Browser tab' },
    { size: 32, label: '32×32', desc: 'Retina displays' },
    { size: 180, label: '180×180', desc: 'Apple touch icon' },
  ] as const;

  const getFaviconHTML = (v: Variant, size: number) => {
    const isDark = v.id === 'light-on-dark' || v.id === 'gold-on-dark';
    const bgColor = isDark ? '#1A1A1A' : '#FFFFFF';
    const textColor = v.id === 'gold-on-dark' ? '#C9A96E' : isDark ? '#FFFFFF' : '#2C2C2C';

    return `<!-- HSG Favicon — ${size}×${size} -->
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100">
  <rect width="100" height="100" fill="${bgColor}" rx="12"/>
  <text x="50" y="62" font-family="Georgia, serif" font-size="48" font-weight="600" 
        letter-spacing="0.05em" fill="${textColor}" text-anchor="middle">HSG</text>
</svg>`;
  };

  const getLinkTags = (size: number) => {
    const tags = [
      `<link rel="icon" type="image/svg+xml" href="/favicon-${size}.svg" />`,
    ];
    if (size === 180) {
      tags.push(`<link rel="apple-touch-icon" sizes="180x180" href="/favicon-180.png" />`);
    }
    return tags.join('\n');
  };

  const handleCopyFaviconCode = async () => {
    const code = getFaviconHTML(activeVariant, selectedSize);
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setFaviconCopied(true);
    setShowFaviconCode(true);
    setTimeout(() => setFaviconCopied(false), 2500);
  };

  const handleDownloadZip = () => {
    setZipDownloading(true);
    const sizes = [16, 32, 180];
    const readme = `HSG Favicon Pack — ${activeVariant.label}
Generated by Higher Standard Global Logo Export Tool

Files included:
  hsg-favicon-16.svg   — 16×16  Browser tab (standard)
  hsg-favicon-32.svg   — 32×32  Retina / HiDPI displays
  hsg-favicon-180.svg  — 180×180 Apple touch icon (home screen)

How to use:
1. Place the SVG files in your website's root directory (or /public/).
2. Add these tags inside your HTML <head>:

   <link rel="icon" type="image/svg+xml" href="/hsg-favicon-32.svg" />
   <link rel="icon" type="image/svg+xml" sizes="16x16" href="/hsg-favicon-16.svg" />
   <link rel="apple-touch-icon" sizes="180x180" href="/hsg-favicon-180.svg" />

3. For PNG fallback compatibility, convert the SVGs at favicon.io or squoosh.app.

Color variant: ${activeVariant.label}
Background:   ${activeVariant.bgColor}
`;

    const files: Record<string, Uint8Array> = {
      'README.txt': strToU8(readme),
    };

    sizes.forEach((size) => {
      const svg = getFaviconHTML(activeVariant, size);
      files[`hsg-favicon-${size}.svg`] = strToU8(svg);
    });

    const zipped = zipSync(files, { level: 6 });
    const blob = new Blob([zipped], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hsg-favicon-pack-${activeVariant.id}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setZipDownloading(false), 1500);
  };

  const isDark = activeVariant.id === 'light-on-dark' || activeVariant.id === 'gold-on-dark';
  const previewBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const previewText = activeVariant.id === 'gold-on-dark' ? '#C9A96E' : isDark ? '#FFFFFF' : '#2C2C2C';

  return (
    <div className="w-full max-w-2xl mb-12">
      <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6E3]">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A] tracking-wide">Favicon Generator</p>
            <p className="text-xs text-[#9A9A9A] mt-0.5">
              Use the HSG monogram as a browser tab icon — SVG format for crisp display at any size
            </p>
          </div>
        </div>

        {/* Preview area */}
        <div className="px-6 py-6 bg-[#FAFAF8] border-b border-[#E8E6E3]">
          <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-4">Live Preview</p>
          
          {/* Browser tab simulation */}
          <div className="bg-white rounded-t-lg border border-[#E8E6E3] border-b-0 overflow-hidden max-w-md mx-auto">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F5F5F5] border-b border-[#E8E6E3]">
              {/* Simulated tab */}
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-t-md border border-[#E8E6E3] border-b-0 -mb-px">
                {/* Favicon preview */}
                <div
                  className="flex items-center justify-center rounded-sm overflow-hidden"
                  style={{
                    width: selectedSize <= 32 ? selectedSize : 32,
                    height: selectedSize <= 32 ? selectedSize : 32,
                    backgroundColor: previewBg,
                    fontSize: selectedSize <= 32 ? selectedSize * 0.5 : 16,
                    color: previewText,
                    fontFamily: 'Georgia, serif',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                  }}
                >
                  {selectedSize <= 32 ? 'H' : 'HSG'}
                </div>
                <span className="text-xs text-[#6B6B6B]">Higher Standard Global</span>
                <i className="ri-close-line text-xs text-[#9A9A9A] ml-1"></i>
              </div>
              {/* New tab button */}
              <div className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8E6E3] cursor-pointer">
                <i className="ri-add-line text-xs text-[#6B6B6B]"></i>
              </div>
            </div>
            {/* Tab content area */}
            <div className="h-16 bg-white"></div>
          </div>

          {/* Size selector */}
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            {faviconSizes.map(({ size, label, desc }) => (
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={`flex flex-col items-center px-4 py-2 rounded-lg border transition-all cursor-pointer ${
                  selectedSize === size
                    ? 'bg-[#2C2C2C] border-[#2C2C2C] text-white'
                    : 'bg-white border-[#E8E6E3] text-[#6B6B6B] hover:border-[#2C2C2C]'
                }`}
              >
                <span className="text-xs font-medium">{label}</span>
                <span className={`text-[10px] mt-0.5 ${selectedSize === size ? 'text-white/70' : 'text-[#9A9A9A]'}`}>
                  {desc}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Code section */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#9A9A9A] uppercase tracking-widest">HTML Link Tags</p>
            <button
              onClick={() => setShowFaviconCode((s) => !s)}
              className="text-xs text-[#8B7355] hover:text-[#2C2C2C] transition-colors cursor-pointer underline underline-offset-2"
            >
              {showFaviconCode ? 'Hide code' : 'Show SVG code'}
            </button>
          </div>

          {/* Link tags preview */}
          <div className="bg-[#1A1A1A] rounded-lg p-4 mb-4">
            <pre className="text-[11px] leading-relaxed text-[#C9A96E] font-mono whitespace-pre-wrap break-all">
              {getLinkTags(selectedSize)}
            </pre>
          </div>

          {/* SVG code (collapsible) */}
          {showFaviconCode && (
            <div className="relative mb-4">
              <pre className="text-[11px] leading-relaxed text-[#4A4A4A] bg-[#FAFAF8] p-4 rounded-lg overflow-x-auto font-mono whitespace-pre border border-[#E8E6E3]">
                {getFaviconHTML(activeVariant, selectedSize)}
              </pre>
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-[#C9A96E] bg-white border border-[#E8E6E3] px-2 py-0.5 rounded-full">
                {selectedSize}×{selectedSize}
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCopyFaviconCode}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                faviconCopied
                  ? 'bg-[#4A7C59] text-white'
                  : 'bg-[#2C2C2C] text-white hover:bg-[#C9A96E]'
              }`}
            >
              <i className={`${faviconCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
              {faviconCopied ? 'Copied!' : 'Copy SVG code'}
            </button>
            <button
              onClick={() => {
                const svg = getFaviconHTML(activeVariant, selectedSize);
                const blob = new Blob([svg], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `hsg-favicon-${selectedSize}.svg`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap bg-white text-[#2C2C2C] border border-[#2C2C2C] hover:bg-[#F7F4EF]"
            >
              <i className="ri-download-line text-sm"></i>
              Download SVG
            </button>
            <button
              onClick={handleDownloadZip}
              disabled={zipDownloading}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap border ${
                zipDownloading
                  ? 'bg-[#C9A96E] text-white border-[#C9A96E]'
                  : 'bg-[#F7F4EF] text-[#8B7355] border-[#C9A96E] hover:bg-[#C9A96E] hover:text-white'
              }`}
            >
              <i className={`${zipDownloading ? 'ri-loader-4-line animate-spin' : 'ri-folder-zip-line'} text-sm`}></i>
              {zipDownloading ? 'Preparing…' : 'Download all sizes as ZIP'}
            </button>
          </div>
        </div>

        {/* Usage tips */}
        <div className="px-6 py-4 bg-[#FAFAF8] border-t border-[#E8E6E3]">
          <p className="text-xs text-[#9A9A9A] leading-relaxed">
            <strong className="text-[#6B6B6B]">ZIP pack includes:</strong> all 3 sizes (16×16, 32×32, 180×180) as SVG files plus a{' '}
            <code className="bg-white px-1 py-0.5 rounded text-[10px] border border-[#E8E6E3]">README.txt</code>{' '}
            with ready-to-paste{' '}
            <code className="bg-white px-1 py-0.5 rounded text-[10px] border border-[#E8E6E3]">&lt;link&gt;</code>{' '}
            tags.&nbsp;&nbsp;
            <strong className="text-[#6B6B6B]">Pro tip:</strong> Drop the SVGs in your site root and paste the link tags into your HTML{' '}
            <code className="bg-white px-1 py-0.5 rounded text-[10px] border border-[#E8E6E3]">&lt;head&gt;</code>.
            For PNG fallback, convert at favicon.io.
          </p>
        </div>
      </div>
    </div>
  );
}

function LogoMark({
  monogram,
  wordmark1,
  wordmark2,
  divider,
  size = 'lg',
}: {
  monogram: string;
  wordmark1: string;
  wordmark2: string;
  divider: string;
  size?: 'sm' | 'lg';
}) {
  const mono = size === 'lg' ? 'text-5xl' : 'text-3xl';
  const word = size === 'lg' ? 'text-[13px]' : 'text-[10px]';
  const divH = size === 'lg' ? 'h-12' : 'h-8';

  return (
    <div className="flex items-center gap-4 select-none">
      <span className={`font-['Cormorant_Garamond'] font-semibold tracking-widest leading-none ${mono} ${monogram}`}>
        HSG
      </span>
      <div className={`w-px ${divH} ${divider}`}></div>
      <div className="flex flex-col justify-center gap-1">
        <span className={`uppercase tracking-[0.22em] font-medium leading-none ${word} ${wordmark1}`}>
          Higher Standard
        </span>
        <span className={`uppercase tracking-[0.35em] font-light leading-none ${word} ${wordmark2}`}>
          Global
        </span>
      </div>
    </div>
  );
}

export default function LogoExportPage() {
  useSEO({
    title: 'Logo Export | Higher Standard Global Brand Tools',
    description: 'Export the HSG logo in SVG, PNG, and favicon formats.',
    canonical: '/logo-export',
    noIndex: true,
  });

  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [activeVariant, setActiveVariant] = useState(variants[0]);
  const previewRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleCopyHint = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSelectVariant = (v: typeof variants[0]) => {
    setActiveVariant(v);
    previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCopyEmbed = async () => {
    const code = buildEmbedCode(activeVariant);
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setEmbedCopied(true);
    setShowEmbed(true);
    setTimeout(() => setEmbedCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-start py-16 px-6">
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-3">Higher Standard Global</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1A1A] mb-4">Logo Export</h1>
        <p className="text-sm text-[#6B6B6B] max-w-md mx-auto leading-relaxed">
          Right-click any logo below and choose <strong>"Save image as…"</strong> or take a screenshot to capture it.
          Each variant is rendered at full resolution for use across all materials.
        </p>
      </div>

      {/* Variant Selector */}
      <div className="flex flex-wrap gap-2 justify-center mb-10">
        {variants.map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveVariant(v)}
            className={`px-4 py-2 rounded-full text-xs tracking-wide font-medium transition-all cursor-pointer whitespace-nowrap border ${
              activeVariant.id === v.id
                ? 'bg-[#2C2C2C] text-white border-[#2C2C2C]'
                : 'bg-white text-[#6B6B6B] border-[#E8E6E3] hover:border-[#2C2C2C] hover:text-[#2C2C2C]'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Large Preview */}
      <div className="mb-6 text-center" ref={previewRef}>
        <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-4">Large — right-click to save</p>
        <div
          className={`inline-flex items-center justify-center px-16 py-12 rounded-lg ${activeVariant.bg} ${activeVariant.border}`}
          style={{ minWidth: 480 }}
        >
          <LogoMark
            monogram={activeVariant.monogram}
            wordmark1={activeVariant.wordmark1}
            wordmark2={activeVariant.wordmark2}
            divider={activeVariant.divider}
            size="lg"
          />
        </div>
      </div>

      {/* Small Preview */}
      <div className="mb-8 text-center">
        <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-4">Small — right-click to save</p>
        <div
          className={`inline-flex items-center justify-center px-10 py-6 rounded-lg ${activeVariant.bg} ${activeVariant.border}`}
          style={{ minWidth: 320 }}
        >
          <LogoMark
            monogram={activeVariant.monogram}
            wordmark1={activeVariant.wordmark1}
            wordmark2={activeVariant.wordmark2}
            divider={activeVariant.divider}
            size="sm"
          />
        </div>
      </div>

      {/* ── Embed Code Panel ── */}
      <div className="w-full max-w-2xl mb-12">
        <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6E3]">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A] tracking-wide">Embed Code</p>
              <p className="text-xs text-[#9A9A9A] mt-0.5">
                Ready-to-paste HTML for email signatures &amp; external sites — uses inline styles, no dependencies
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowEmbed((s) => !s)}
                className="text-xs text-[#8B7355] hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap underline underline-offset-2"
              >
                {showEmbed ? 'Hide code' : 'Preview code'}
              </button>
              <button
                onClick={handleCopyEmbed}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                  embedCopied
                    ? 'bg-[#4A7C59] text-white'
                    : 'bg-[#2C2C2C] text-white hover:bg-[#C9A96E]'
                }`}
              >
                <i className={`${embedCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                {embedCopied ? 'Copied!' : 'Copy embed code'}
              </button>
            </div>
          </div>

          {/* Code preview (collapsible) */}
          {showEmbed && (
            <div className="relative">
              <pre className="text-[11px] leading-relaxed text-[#4A4A4A] bg-[#FAFAF8] p-6 overflow-x-auto font-mono whitespace-pre">
                {buildEmbedCode(activeVariant)}
              </pre>
              {/* Variant badge */}
              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-[#C9A96E] bg-white border border-[#E8E6E3] px-2 py-0.5 rounded-full">
                {activeVariant.label}
              </span>
            </div>
          )}

          {/* Usage tips */}
          <div className="px-6 py-4 bg-[#FAFAF8] border-t border-[#E8E6E3]">
            <p className="text-xs text-[#9A9A9A] leading-relaxed">
              <strong className="text-[#6B6B6B]">Email signatures:</strong> paste into Gmail / Outlook HTML signature editor.&nbsp;&nbsp;
              <strong className="text-[#6B6B6B]">External sites:</strong> drop the snippet anywhere in your HTML — no CSS file needed.&nbsp;&nbsp;
              <strong className="text-[#6B6B6B]">Tip:</strong> switch variants above and copy again to get a different color scheme.
            </p>
          </div>
        </div>
      </div>

      {/* ── Favicon Generator Section ── */}
      <FaviconGenerator activeVariant={activeVariant} />

      {/* All Variants Grid */}
      <div className="w-full max-w-4xl">
        <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-6 text-center">All Variants</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {variants.map((v) => (
            <div key={v.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#9A9A9A] tracking-wide">{v.label}</p>
                {activeVariant.id === v.id && (
                  <span className="text-[10px] uppercase tracking-widest text-[#C9A96E] font-medium">Selected</span>
                )}
              </div>
              <div
                onClick={() => handleSelectVariant(v)}
                className={`flex items-center justify-center px-10 py-8 rounded-lg ${v.bg} ${v.border} cursor-pointer transition-all duration-200 ${
                  activeVariant.id === v.id
                    ? 'ring-2 ring-[#C9A96E] ring-offset-2 ring-offset-[#F7F4EF]'
                    : 'hover:ring-1 hover:ring-[#C9A96E]/50 hover:ring-offset-1 hover:ring-offset-[#F7F4EF]'
                }`}
                title="Click to select · Right-click → Save image as…"
              >
                <LogoMark
                  monogram={v.monogram}
                  wordmark1={v.wordmark1}
                  wordmark2={v.wordmark2}
                  divider={v.divider}
                  size="sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-16 max-w-lg w-full bg-white rounded-lg border border-[#E8E6E3] p-8">
        <h2 className="font-['Cormorant_Garamond'] text-xl text-[#1A1A1A] mb-4">How to save the logo</h2>
        <ol className="space-y-3 text-sm text-[#6B6B6B] leading-relaxed">
          <li className="flex gap-3">
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-xs flex-shrink-0 mt-0.5">1</span>
            <span>Select the variant you want using the buttons above.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-xs flex-shrink-0 mt-0.5">2</span>
            <span><strong>Right-click</strong> the logo preview and choose <strong>"Save image as…"</strong> — or use your browser's screenshot tool.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-xs flex-shrink-0 mt-0.5">3</span>
            <span>For a transparent PNG, use a screenshot tool and crop tightly around the logo, then remove the background in Canva, Photoshop, or <strong>remove.bg</strong>.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-xs flex-shrink-0 mt-0.5">4</span>
            <span>Once you have the image file, paste the URL here and I'll update it everywhere on the site instantly.</span>
          </li>
        </ol>
      </div>

      {/* Footer links */}
      <div className="mt-10 flex items-center gap-6">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="text-xs text-[#9A9A9A] hover:text-[#2C2C2C] transition-colors tracking-wide cursor-pointer"
        >
          ← Back to site
        </a>
        <span className="text-[#E8E6E3]">|</span>
        <a
          href="/brand"
          onClick={(e) => { e.preventDefault(); navigate('/brand'); }}
          className="text-xs text-[#8B7355] hover:text-[#2C2C2C] transition-colors tracking-wide cursor-pointer"
        >
          Brand Guidelines →
        </a>
        <span className="text-[#E8E6E3]">|</span>
        <a
          href="/business-card"
          onClick={(e) => { e.preventDefault(); navigate('/business-card'); }}
          className="text-xs text-[#8B7355] hover:text-[#2C2C2C] transition-colors tracking-wide cursor-pointer"
        >
          Business Card →
        </a>
      </div>
    </div>
  );
}
